/**
 * A CSV reader and writer with no dependency, for the bulk imports.
 *
 * RFC 4180 with the leniencies real exports need: a UTF-8 byte order mark is
 * skipped, CRLF, LF and a lone CR all end a record, a quote is only special at
 * the start of a field, and a record with nothing in it is dropped wherever it
 * sits. Fields are returned untrimmed; what to do with whitespace is the
 * caller's decision.
 *
 * Every record carries the physical line it starts on, counted from 1 with the
 * header on line 1, because that number is how somebody finds a bad row in
 * their spreadsheet. A quoted field that spans lines advances the count so the
 * records after it stay right.
 */

export interface CsvRecord {
  /** Physical 1-based line the record starts on. */
  line: number;
  fields: string[];
}

export interface CsvProblem {
  line: number;
  message: string;
}

export function parseCsv(text: string): { records: CsvRecord[]; problems: CsvProblem[] } {
  const source = text.charAt(0) === '\uFEFF' ? text.slice(1) : text;
  const records: CsvRecord[] = [];
  const problems: CsvProblem[] = [];

  let line = 1;
  let recordLine = 1;
  let fields: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  const endField = (): void => {
    fields.push(field);
    field = '';
  };
  const endRecord = (): void => {
    endField();
    if (fields.some((value) => value.trim() !== '')) records.push({ line: recordLine, fields });
    fields = [];
    recordLine = line;
  };

  while (i < source.length) {
    const ch = source.charAt(i);

    if (quoted) {
      if (ch === '"') {
        if (source.charAt(i + 1) === '"') {
          field += '"';
          i += 2;
        } else {
          quoted = false;
          i += 1;
        }
      } else if (ch === '\r' || ch === '\n') {
        field += '\n';
        line += 1;
        i += ch === '\r' && source.charAt(i + 1) === '\n' ? 2 : 1;
      } else {
        field += ch;
        i += 1;
      }
      continue;
    }

    if (ch === '"' && field === '') {
      quoted = true;
      i += 1;
    } else if (ch === ',') {
      endField();
      i += 1;
    } else if (ch === '\r' || ch === '\n') {
      i += ch === '\r' && source.charAt(i + 1) === '\n' ? 2 : 1;
      line += 1;
      endRecord();
    } else {
      field += ch;
      i += 1;
    }
  }

  if (quoted) {
    // The partial record is dropped rather than guessed at.
    problems.push({ line: recordLine, message: 'A quoted value is never closed.' });
  } else if (field !== '' || fields.length > 0) {
    endRecord();
  }

  return { records, problems };
}

// Quotes for the CSV grammar only. It does not neutralise formula prefixes
// (=, +, -, @), so it must not back an export of user-supplied values without
// doing so; today it only ever writes the constant template.
const quoteField = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

/** CRLF line ends, and a field is quoted only when it has to be. */
export function formatCsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(quoteField).join(',')).join('\r\n') + '\r\n';
}

/** `First Name`, `first-name` and `FIRST_NAME` all name the column `first_name`. */
export function normalizeHeader(cell: string): string {
  return cell
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}
